import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'cccisd-loader';
import notification from 'cccisd-notification';
import Tooltip from 'cccisd-tooltip';
import axios from 'cccisd-axios';
import Button from 'cccisd-click-button';
import {
    useGetUsersQuery,
    useGetStepsAssignmentQuery,
    useAllStepsDataQuery,
    useLogicModelQueryInvalidation,
} from 'js/queries';
import { setCurrentDeployment } from 'js/reducers/egto.js';
import ModalButton from 'js/components/ModalButton';
import SurveyPlayer from './SurveyPlayer.js';
import Step3Report from 'js/vendor/reports/reportTemplates/Step3Comparison';
import Step4Report from 'js/vendor/reports/reportTemplates/Step4Comparison';
import Step5Report from 'js/vendor/reports/reportTemplates/Step5Comparison';
import { AnswerSummary } from 'cccisd-laravel-appdefs/dist/reportTemplates';
import { getFlowListInfo } from './helpers.js';
import style from './style.css';
import _range from 'lodash/range';

const appDefs = window.cccisd.appDefs;
const Boilerplate = window.cccisd.boilerplate;

const Survey = props => {
    const [loading, setLoading] = useState(true);
    const [showReportOrSurvey, setShowReportOrSurvey] = useState('survey');
    const [closingModal, setClosingModal] = useState(false);
    const dispatch = useDispatch();
    const { invalidateLogicModelQueries } = useLogicModelQueryInvalidation();

    /*
     *   Queries
     */
    const getUsersQuery = useGetUsersQuery();
    const stepsAssignment = useGetStepsAssignmentQuery(
        `stepsAssignment ${props.deployment.deploymentId}`,
        props.metricspawn.pawnId,
        props.deployment.deploymentId
    );

    const stepProgressData = useMemo(() => {
        const surveyList = stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList;
        if (!surveyList) return null;

        return {
            thereIsStep2Progress: surveyList[1]?.flow.flowProgress.status,
            thereIsStep3Progress: surveyList[2]?.flow.flowProgress.status,
            thereIsStep4Progress: surveyList[3]?.flow.flowProgress.status,
            thereIsStep5Progress: surveyList[4]?.flow.flowProgress.status,
            completedFlowSteps: surveyList.filter(x => x.flow.flowProgress.status === 'Complete'),
        };
    }, [stepsAssignment?.data?.data?.flows.deployment.assignment.surveyList]);

    const thereIsStep2Progress = stepProgressData?.thereIsStep2Progress;
    const thereIsStep3Progress = stepProgressData?.thereIsStep3Progress;
    const thereIsStep4Progress = stepProgressData?.thereIsStep4Progress;
    const thereIsStep5Progress = stepProgressData?.thereIsStep5Progress;
    const completedFlowSteps = stepProgressData?.completedFlowSteps;

    // Use the consolidated allStepsData query
    const allStepsDataQuery = useAllStepsDataQuery(
        `allStepsData ${props.deployment.deploymentId}`,
        props.metricspawn.pawnId,
        props.deployment.deploymentId
    );

    // Extract structured data from the consolidated query
    const stepsData = useMemo(() => {
        const devTags = allStepsDataQuery?.data?.data?.roles?.metricspawn?.assignmentProgress?.devTags;
        if (!devTags) return null;

        // Helper function to structure alert data for compatibility with helpers.js
        const structureAlertsData = stepPrefix => {
            const alerts = {};
            const alertsResolve = {};

            // Extract all keys that match the step pattern
            Object.keys(devTags).forEach(key => {
                if (key.startsWith(stepPrefix)) {
                    if (key.includes('_resolve')) {
                        alertsResolve[key] = devTags[key];
                    } else {
                        alerts[key] = devTags[key];
                    }
                }
            });

            // Map step prefixes to proper step names
            const stepMap = {
                s2_: 'step2',
                s3_: 'step3',
                s4_: 'step4',
                s5_: 'step5',
            };
            const stepName = stepMap[stepPrefix];

            return {
                data: {
                    data: {
                        roles: {
                            metricspawn: {
                                assignmentProgress: {
                                    [`${stepName}Alerts`]: alerts,
                                    [`${stepName}AlertsResolve`]: alertsResolve,
                                },
                            },
                        },
                    },
                },
            };
        };

        return {
            listVarsData: {
                data: {
                    data: {
                        roles: {
                            metricspawn: {
                                assignmentProgress: {
                                    devTags,
                                    // Extract data structures that SurveyPlayer expects
                                    goals: Object.keys(devTags)
                                        .filter(key => key.startsWith('s2_goal_') && !key.includes('_outcome_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    outcomes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s2_') && key.includes('_outcome_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    selectedOutcomes: Object.keys(devTags)
                                        .filter(key => key.includes('_selected_outcomes'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    proceed: Object.keys(devTags)
                                        .filter(key => key.includes('_proceed'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    selectedGoals: Object.keys(devTags)
                                        .filter(key => key.includes('_selected_goals'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    interventions: Object.keys(devTags)
                                        .filter(key => key.startsWith('step3_') && key.includes('_name'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    // Step 4 adaptations by domain
                                    targetAdaptations: Object.keys(devTags)
                                        .filter(key => key.includes('_fit_target_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    communityAdaptations: Object.keys(devTags)
                                        .filter(key => key.includes('_fit_community_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    orgAdaptations: Object.keys(devTags)
                                        .filter(key => key.includes('_fit_organization_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    // Step 5 changes by domain
                                    staffChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_staff_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    commitmentChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_lead_commitment_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    communicationChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_lead_communication_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    techChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_tech_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    materialChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_financial_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    collabChanges: Object.keys(devTags)
                                        .filter(key => key.includes('_cap_collab_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    // Step 4 adaptations by intervention
                                    intervention1Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i1_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention2Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i2_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention3Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i3_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention4Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i4_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention5Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i5_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention6Adaptations: Object.keys(devTags)
                                        .filter(key => key.startsWith('s4_i6_fit_') && key.includes('_ad_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    // Step 5 changes by intervention
                                    intervention1Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i1_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention2Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i2_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention3Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i3_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention4Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i4_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention5Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i5_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                    intervention6Changes: Object.keys(devTags)
                                        .filter(key => key.startsWith('s5_i6_cap_') && key.includes('_task_'))
                                        .reduce((acc, key) => ({ ...acc, [key]: devTags[key] }), {}),
                                },
                            },
                        },
                    },
                },
            },
            step2Alerts: structureAlertsData('s2_'),
            step3Alerts: structureAlertsData('s3_'),
            step4Alerts: structureAlertsData('s4_'),
            step5Alerts: structureAlertsData('s5_'),
        };
    }, [allStepsDataQuery?.data]);

    // Create mock query objects to maintain compatibility with existing code
    const step2ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
            refetch: allStepsDataQuery.refetch,
        }),
        [
            stepsData?.listVarsData?.data,
            allStepsDataQuery.isError,
            allStepsDataQuery.isLoading,
            allStepsDataQuery.refetch,
        ]
    );

    const step3ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
            refetch: allStepsDataQuery.refetch,
        }),
        [
            stepsData?.listVarsData?.data,
            allStepsDataQuery.isError,
            allStepsDataQuery.isLoading,
            allStepsDataQuery.refetch,
        ]
    );

    const step4ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
            refetch: allStepsDataQuery.refetch,
        }),
        [
            stepsData?.listVarsData?.data,
            allStepsDataQuery.isError,
            allStepsDataQuery.isLoading,
            allStepsDataQuery.refetch,
        ]
    );

    const step5ListVarsQuery = useMemo(
        () => ({
            data: stepsData?.listVarsData?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
            refetch: allStepsDataQuery.refetch,
        }),
        [
            stepsData?.listVarsData?.data,
            allStepsDataQuery.isError,
            allStepsDataQuery.isLoading,
            allStepsDataQuery.refetch,
        ]
    );

    const step2AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step2Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step2Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step3AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step3Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step3Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step4AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step4Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step4Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const step5AlertsQuery = useMemo(
        () => ({
            data: stepsData?.step5Alerts?.data,
            isError: allStepsDataQuery.isError,
            isLoading: allStepsDataQuery.isLoading,
        }),
        [stepsData?.step5Alerts?.data, allStepsDataQuery.isError, allStepsDataQuery.isLoading]
    );

    const updateReviewSteps = useCallback(
        async (stepsToReview, shouldRefetch = true) => {
            const { deployment, metricspawn } = props;

            if (stepsToReview) {
                const problemData = {
                    sourceId: deployment.assignmentId,
                    sourceType: 'assignment',
                    values: JSON.stringify({ review_steps: stepsToReview }),
                    pawnId: metricspawn.pawnId,
                    pawnHash: metricspawn.pawnHash,
                    assignmentProgressId: deployment.assignmentProgressId,
                    responseSet: deployment.deploymentId,
                };

                const response = await axios.post(Boilerplate.route('metrics.api.v1.data.bulkUpdate'), problemData);

                if (response && response.data.status === 'success' && shouldRefetch) {
                    await props.assignmentProgressQuery.refetch();
                }
            }
        },
        [props.deployment, props.metricspawn, props.assignmentProgressQuery]
    );

    const getReviewSteps = useCallback(
        thisSurvey => {
            let stepsToReview = [];

            if (completedFlowSteps && props.deployment.hasReviewed.has_reviewed !== '1') {
                const mostRecentFlow = thisSurvey ? thisSurvey.surveyHandle : props.deployment.mostRecentFlow;
                const indexOfMostRecentFlow = completedFlowSteps.map(flow => flow.surveyHandle).indexOf(mostRecentFlow);
                if (indexOfMostRecentFlow !== -1) {
                    stepsToReview = _range(indexOfMostRecentFlow + 2, completedFlowSteps.length + 1);
                }

                return stepsToReview;
            }
        },
        [completedFlowSteps, props.deployment]
    );

    const updateHasReviewed = useCallback(
        async hasReviewed => {
            const { deployment, metricspawn } = props;

            const problemData = {
                sourceId: deployment.assignmentId,
                sourceType: 'assignment',
                values: JSON.stringify({ has_reviewed: hasReviewed }),
                pawnId: metricspawn.pawnId,
                pawnHash: metricspawn.pawnHash,
                assignmentProgressId: deployment.assignmentProgressId,
                responseSet: deployment.deploymentId,
            };

            const response = await axios.post(Boilerplate.route('metrics.api.v1.data.bulkUpdate'), problemData);

            if (response) {
                if (response.data.status === 'success') {
                    await props.assignmentProgressQuery.refetch();
                }
            }
        },
        [props.deployment, props.metricspawn, props.assignmentProgressQuery]
    );

    useEffect(() => {
        if (!stepsAssignment.data) return;

        setLoading(true);
        (async () => {
            try {
                const reviewStepsToUpdate = getReviewSteps();
                if (reviewStepsToUpdate && reviewStepsToUpdate.length > 0) {
                    await updateReviewSteps(reviewStepsToUpdate, false);
                }
            } catch (error) {
                console.error('Error updating review steps:', error);
            }
            setLoading(false);
        })();
    }, [stepsAssignment.data]);

    const isLoading = getUsersQuery.isLoading || stepsAssignment.isLoading || allStepsDataQuery.isLoading;
    const allQueryDataReturned = getUsersQuery.data && stepsAssignment.data && allStepsDataQuery.data;

    const queryErrorStates = useMemo(() => {
        return [
            getUsersQuery.isError,
            stepsAssignment.isError,
            props.assignmentProgressQuery.isError,
            allStepsDataQuery.isError,
        ];
    }, [
        getUsersQuery.isError,
        stepsAssignment.isError,
        props.assignmentProgressQuery.isError,
        allStepsDataQuery.isError,
    ]);

    const beforeShowSurvey = useCallback(
        async (survey, flowListIndex) => {
            setLoading(true);
            if (survey.status === 'Complete') {
                setShowReportOrSurvey('report');
            }
            dispatch(setCurrentDeployment(props.deployment));

            // Since we're using consolidated query, just refetch it if needed
            try {
                if (!allStepsDataQuery.data) {
                    await allStepsDataQuery.refetch();
                }
            } catch (error) {
                console.error('Error refetching allStepsData query:', error);
            }

            setLoading(false);
        },
        [dispatch, props.deployment, allStepsDataQuery]
    );

    const onCloseSurvey = useCallback(
        async thisSurvey => {
            setLoading(true);
            setClosingModal(true);

            try {
                invalidateLogicModelQueries(props.deployment.deploymentId);
                await updateReviewSteps(getReviewSteps(thisSurvey));
                await allStepsDataQuery.refetch();
                // Small delay to allow query invalidation to trigger
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (error) {
                console.error('Error invalidating queries:', error);
            }

            setLoading(false);
        },
        [
            props.deployment.deploymentId,
            invalidateLogicModelQueries,
            updateReviewSteps,
            getReviewSteps,
            allStepsDataQuery,
        ]
    );

    const getButtonStyle = flowIndex => {
        const colors = ['#6a6eaf', '#353863', '#c6002e', '#7e141d', '#1e818a', '#176168'];
        return { borderLeft: `8px solid ${colors[flowIndex]}`, width: '105px' };
    };

    const getReportSettings = (reportHandle, reportId) => {
        const reportList = appDefs.reports.list;
        // Generic Report
        if (reportHandle === 'answerSummary') {
            const anyReportWithId = reportList.find(r => r.id === reportId);
            return anyReportWithId.reportTemplateSettings;
        }

        const anyReportWithHandle = reportList.find(r => r.reportTemplateHandle === reportHandle);
        if (anyReportWithHandle) {
            return anyReportWithHandle.reportTemplateSettings;
        }
    };

    const renderIfSurveyComplete = (survey, index) => {
        // Names of reports TODO: might need changing
        const reportIds = {
            0: 'PriorityProblemSummary',
            1: 'GoalsSummary',
            2: 'Step3',
            3: 'Intervention1FitSummary',
            4: 'Intervention1CapacitySummary',
            5: 'Step6Report',
        };
        if (!loading) {
            /*
             *   survey.status is still "incomplete" at this point, although the flow is complete.
             *   But we can show the report here anyways because showSurveyOrReport state is changed onFlowComplete
             */

            if (showReportOrSurvey === 'report') {
                if (index === 2) {
                    return (
                        <Step3Report.player
                            settings={getReportSettings('step3')}
                            deploymentId={props.deployment.deploymentId}
                            metricspawn={props.metricspawn}
                        />
                    );
                }
                if (index === 3) {
                    return (
                        <Step4Report.player
                            settings={getReportSettings('step4')}
                            deploymentId={props.deployment.deploymentId}
                            metricspawn={props.metricspawn}
                        />
                    );
                }
                if (index === 4) {
                    return (
                        <Step5Report.player
                            settings={getReportSettings('step5')}
                            deploymentId={props.deployment.deploymentId}
                            metricspawn={props.metricspawn}
                        />
                    );
                }

                return (
                    <div className={style.centerReport}>
                        <AnswerSummary.player
                            settings={getReportSettings('answerSummary', reportIds[index])}
                            deploymentId={props.deployment.deploymentId}
                            pawnId={props.metricspawn.pawnId}
                        />
                    </div>
                );
            }
            if (!props.orgTeamMemberEditingDisabled) {
                return (
                    <>
                        <SurveyPlayer
                            deployment={props.deployment}
                            surveyList={[survey.surveyHandle]}
                            metricspawn={props.metricspawn}
                            getUsersQuery={getUsersQuery}
                            flowListIndex={index}
                            step2ListVarsQuery={step2ListVarsQuery}
                            step3ListVarsQuery={step3ListVarsQuery}
                            step4ListVarsQuery={step4ListVarsQuery}
                            step5ListVarsQuery={step5ListVarsQuery}
                            setShowReportOrSurvey={setShowReportOrSurvey}
                        />
                    </>
                );
            }
        }
    };

    const getButtonDisabled = (flowListInfo, survey) => {
        const survey1Status = flowListInfo.find(s => s.surveyHandle === 'survey1').status;

        if (props.orgTeamMemberEditingDisabled && survey.status !== 'Complete') {
            return true;
        }

        if (survey.surveyHandle !== 'survey1' && survey1Status !== 'Complete') {
            return true;
        }
    };

    if (isLoading) {
        return <Loader loading={isLoading} type="inline" />;
    }

    if (allQueryDataReturned) {
        const flows = stepsAssignment.data.data?.flows.deployment.assignment.surveyList;
        const flowListInfo = getFlowListInfo({
            flowList: flows,
            step2AlertsQuery,
            thereIsStep2Progress,
            step3AlertsQuery,
            thereIsStep3Progress,
            step4AlertsQuery,
            thereIsStep4Progress,
            step5AlertsQuery,
            thereIsStep5Progress,
            deploymentData: props.deployment,
        });
        const reviewSteps = JSON.parse(props.deployment.reviewSteps?.review_steps);

        return (
            <div className={style.stepButtonContainer}>
                {[...Array(47)].map((x, i) => (
                    <div className={style.chevronArrow} key={i} style={{ left: i * 15 }} />
                ))}
                {flowListInfo.map((survey, i) => {
                    return (
                        <div key={survey.surveyHandle} className={style.stepButton}>
                            <ModalButton
                                buttonContent={`Step ${i + 1}`}
                                modalTitle={`Step ${i + 1}`}
                                className="btn btn-secondary"
                                style={getButtonStyle(i)}
                                icon={survey.icon}
                                tooltip={survey.tooltip}
                                beforeShow={() => beforeShowSurvey(survey, i)}
                                beforeClose={() => onCloseSurvey(survey)}
                                afterClose={() => {
                                    setClosingModal(false);
                                    if (!props.orgTeamMemberEditingDisabled) {
                                        setShowReportOrSurvey('survey');
                                    }
                                }}
                                disabled={getButtonDisabled(flowListInfo, survey)}
                                closingModal={closingModal}
                            >
                                {survey.status === 'Complete' || showReportOrSurvey === 'report' ? (
                                    <div>
                                        {!props.orgTeamMemberEditingDisabled && (
                                            <div className={style.surveyCompleteButtons}>
                                                {survey.icon === 'review' && (
                                                    <Tooltip title="Because previous steps have changed, this step has been marked for review. Once you have reviewed this step, click here to mark it as reviewed.">
                                                        <Button
                                                            title="Mark as Reviewed"
                                                            isConfirm
                                                            confirmationMessage="Mark this step as reviewed?"
                                                            className="btn btn-primary"
                                                            onClick={() => {
                                                                updateReviewSteps(reviewSteps.filter(s => s !== i + 1));
                                                                updateHasReviewed(true);
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                                <button
                                                    type="button"
                                                    className={`btn ${
                                                        showReportOrSurvey === 'report'
                                                            ? 'btn-primary'
                                                            : 'btn-secondary'
                                                    }`}
                                                    style={{ marginLeft: 'auto' }}
                                                    disabled={showReportOrSurvey === 'report'}
                                                    onClick={() => setShowReportOrSurvey('report')}
                                                >
                                                    View Report
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn ${
                                                        showReportOrSurvey === 'survey'
                                                            ? 'btn-primary'
                                                            : 'btn-secondary'
                                                    }`}
                                                    disabled={showReportOrSurvey === 'survey'}
                                                    onClick={() => setShowReportOrSurvey('survey')}
                                                >
                                                    Edit Step
                                                </button>
                                            </div>
                                        )}
                                        {!loading && <div>{renderIfSurveyComplete(survey, i)}</div>}
                                    </div>
                                ) : (
                                    <SurveyPlayer
                                        deployment={props.deployment}
                                        surveyList={[survey.surveyHandle]}
                                        metricspawn={props.metricspawn}
                                        getUsersQuery={getUsersQuery}
                                        flowListIndex={i}
                                        step2ListVarsQuery={step2ListVarsQuery}
                                        step3ListVarsQuery={step3ListVarsQuery}
                                        step4ListVarsQuery={step4ListVarsQuery}
                                        step5ListVarsQuery={step5ListVarsQuery}
                                        setShowReportOrSurvey={setShowReportOrSurvey}
                                    />
                                )}
                            </ModalButton>
                        </div>
                    );
                })}
            </div>
        );
    }

    if (queryErrorStates.some(x => x === true)) {
        return (
            <div>
                {notification({
                    message: `An error has occurred.`,
                    type: 'danger',
                    duration: 8000,
                })}
            </div>
        );
    }
};

export default Survey;
